/**
 * Engine entry point.
 *
 * Builds to one self-contained IIFE bundle (`funnel-engine.iife.js`). A host
 * page provides the funnel config and a mount element; the engine renders the
 * funnel into it.
 *
 * Auto-mount: if the page defines `window.FUNNEL_CONFIG` and contains an
 * element with id `funnel-root`, the engine mounts on DOM ready. Otherwise a
 * host can call `FunnelEngine.mountFunnel(config, element)` explicitly.
 */
import { h, render } from "preact";
import type { FunnelConfig } from "./types";
import { injectStyles } from "./renderer/theme";
import { EventBus } from "./analytics/eventBus";
import { D1Sink, type D1SinkContext } from "./analytics/d1Sink";
import { getOrCreateSessionId } from "./state/progress";
import { FunnelApp } from "./renderer/FunnelApp";

declare global {
  interface Window {
    FUNNEL_CONFIG?: FunnelConfig;
  }
}

// W1-C1 — beacon endpoints. Absolute URLs (cross-origin from check.truecasenetwork.com
// to api.truecasenetwork.com). Hardcoded for Wave 1 single-funnel; Wave 2 should extract
// to config.analytics.d1_sink.{endpoint, fail_counter_endpoint}.
const FUNNEL_EVENTS_ENDPOINT = "https://api.truecasenetwork.com/api/funnel_events";
const BEACON_FAIL_ENDPOINT = "https://api.truecasenetwork.com/api/_health/beacon_fail";

const VISITOR_ID_STORAGE_KEY = "fe_visitor_id";

/** Get-or-create a persistent visitor_id. localStorage if available; sessionStorage
 *  fallback for incognito; in-memory generated value if both unavailable.
 *  Per FOUNDATION Lock 3 (localStorage-sticky, never cookies). */
function getOrCreateVisitorId(): string {
  const generate = (): string =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `vis-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generate();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // ITP / privacy mode — fall through to sessionStorage
  }
  try {
    const existing = window.sessionStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generate();
    window.sessionStorage.setItem(VISITOR_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Storage fully unavailable — generate ephemeral id per-mount.
  }
  return generate();
}

/** Build D1Sink context from URL params + window state at mount time. */
function buildD1SinkContext(funnelId: string, sessionId: string, visitorId: string): D1SinkContext {
  const ctx: D1SinkContext = {
    funnel_id: funnelId,
    funnel_variant_id: "default",  // Wave 1: single variant. Wave 2 adds funnel-level A/B per FOUNDATION Lock 6.
    session_id: sessionId,
    visitor_id: visitorId,
  };
  // Capture UTM + Meta ad attribution from URL query.
  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
                     "meta_campaign_id", "meta_adset_id", "meta_ad_id", "meta_placement",
                     "angle", "hook", "h", "o", "p"] as const;
    for (const k of utmKeys) {
      const v = params.get(k);
      if (v !== null) (ctx as unknown as Record<string, unknown>)[k] = v;
    }
  } catch {
    // URL parsing fail — proceed without UTMs (sink writes still go through; UTM cols stay NULL).
  }
  // Device context.
  try {
    if (typeof navigator !== "undefined") {
      ctx.browser = navigator.userAgent;  // raw UA; D1 stores as text
      const ua = navigator.userAgent;
      ctx.device_type = /\bMobi|Android(?!.*\bTablet)|iPhone|iPod/i.test(ua)
        ? "mobile"
        : /iPad|Tablet|Android/i.test(ua)
          ? "tablet"
          : "desktop";
    }
    if (typeof window !== "undefined") {
      ctx.viewport_width = window.innerWidth || undefined;
      ctx.viewport_height = window.innerHeight || undefined;
    }
  } catch {
    // navigator unavailable in some sandboxed contexts.
  }
  return ctx;
}

export function mountFunnel(config: FunnelConfig, container: HTMLElement): void {
  injectStyles(config.theme);
  const sessionId = getOrCreateSessionId(config.funnel_id);
  const visitorId = getOrCreateVisitorId();

  // W1-C1 — instantiate D1Sink for the funnel_events beacon path.
  const d1SinkContext = buildD1SinkContext(config.funnel_id, sessionId, visitorId);
  const d1Sink = new D1Sink(FUNNEL_EVENTS_ENDPOINT, BEACON_FAIL_ENDPOINT, d1SinkContext);

  const bus = new EventBus(config.funnel_id, sessionId, config.analytics, config.settings, d1Sink);
  bus.init();
  container.classList.add("fe-root");
  render(h(FunnelApp, { config, sessionId, bus }), container);
}

function autoMount(): void {
  const config = window.FUNNEL_CONFIG;
  const el = document.getElementById("funnel-root");
  if (config && el) mountFunnel(config, el);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMount);
} else {
  autoMount();
}
